import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Stack,
  IconButton,
  InputAdornment,
  TextField,
  Checkbox,
  FormControlLabel,
  Alert,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import LockOutlined from "@mui/icons-material/LockOutlined";
import ShieldOutlined from "@mui/icons-material/ShieldOutlined";
import { login } from "../../services/AuthService";
import { UserContextType } from "../../models/User";
import { useFormik } from "formik";
import UserContext from "../../context/UserContext";

export default function Login() {
  const navigate = useNavigate();
  const emailFromStorage = localStorage.getItem("email") || "";
  const passwordFromStorage = localStorage.getItem("password") || "";
  const rememberFromStorage = localStorage.getItem("remember") === "t" || false;
  const [remember, setRemember] = useState(rememberFromStorage);
  const [loginMessageError, setLoginMessageError] = useState("");
  const { setUser } = useContext<UserContextType | null>(UserContext) as UserContextType;
  const [showPassword, setShowPassword] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: emailFromStorage,
      password: passwordFromStorage,
    },
    validate: (values) => {
      const errors: any = {};
      if (!values.email) errors.email = "Email is required";
      if (!values.password) errors.password = "Password is required";
      return errors;
    },
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);
        setLoginMessageError("");
        const res = await login(values.email, values.password);
        if (res.success) {
          setUser(res.user?.email as string);
          localStorage.setItem("user", JSON.stringify(res.user, null, 2));
          localStorage.setItem("access_token", res.access_token as string);
          if (remember) {
            localStorage.setItem("email", values.email);
            localStorage.setItem("password", values.password);
            localStorage.setItem("remember", "t");
          } else {
            localStorage.removeItem("email");
            localStorage.removeItem("password");
            localStorage.removeItem("remember");
          }
          navigate("/", { replace: true });
        } else {
          setLoginMessageError(res.message as string);
        }
      } catch (e) {
        setLoginMessageError("Oops, Something went wrong. Please try again.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1.15fr 0.85fr", lg: "1.25fr 0.75fr" },
        minHeight: "100vh",
        backgroundColor: "#0B0F19",
        backgroundImage: `linear-gradient(180deg, rgba(5, 10, 20, 0.5) 0%, rgba(2, 6, 12, 0.75) 100%), url("/login-bg.png")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Left Brand Panel: Official Executive Branding */}
      <Box
        sx={{
          backgroundColor: "transparent",
          color: "#FFFFFF",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          minHeight: { xs: 260, md: "100vh" },
          p: { xs: 4, sm: 6, md: 8 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Subtle decorative glow */}
        <Box
          sx={{
            position: "absolute",
            width: "500px",
            height: "500px",
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(0, 174, 239, 0.15) 0%, rgba(0,0,0,0) 70%)",
            top: "-150px",
            right: "-150px",
            pointerEvents: "none",
          }}
        />

        {/* Top Header / Insignia */}
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              component="img"
              src="/mtrcb-admin-logo.png"
              alt="MTRCB Admin Logo"
              sx={{
                width: { xs: 64, md: 80 },
                height: "auto",
                filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.25))",
              }}
            />
            <Box>
              <Typography
                sx={{
                  fontSize: { xs: 11, md: 12 },
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  color: "rgba(255,255,255,0.7)",
                  textTransform: "uppercase",
                }}
              >
                Republic of the Philippines
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: 16, md: 19 },
                  fontWeight: 800,
                  letterSpacing: "-0.01em",
                  color: "#FFFFFF",
                  lineHeight: 1.25,
                }}
              >
                MTRCB Mobile App Admin Portal
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Center Hero Statement */}
        <Box
          sx={{
            display: { xs: "none", md: "block" },
            maxWidth: 560,
            position: "relative",
            zIndex: 1,
            my: "auto",
            py: 6,
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.5,
              borderRadius: "4px",
              backgroundColor: "rgba(255, 255, 255, 0.12)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              mb: 2.5,
            }}
          >
            <ShieldOutlined sx={{ fontSize: 16, color: "#00AEEF" }} />
            <Typography sx={{ fontSize: 12, fontWeight: 650, letterSpacing: "0.03em" }}>
              OFFICIAL ADMINISTRATIVE SYSTEM
            </Typography>
          </Box>

          <Typography
            sx={{
              fontSize: { md: 28, lg: 34 },
              fontWeight: 800,
              letterSpacing: "-0.02em",
              lineHeight: 1.25,
              color: "#FFFFFF",
            }}
          >
            Movie and Television Review and Classification Board
          </Typography>

          <Typography
            sx={{
              color: "rgba(255,255,255,0.8)",
              mt: 2,
              fontSize: 15,
              lineHeight: 1.65,
            }}
          >
            Centralized portal for deputy report management, cinema screening verifications,
            classification monitoring, and administrative compliance.
          </Typography>
        </Box>

        {/* Bottom Compliance Advisory */}
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Divider sx={{ borderColor: "rgba(255,255,255,0.15)", mb: 2 }} />
          <Typography
            sx={{
              color: "rgba(255,255,255,0.6)",
              fontSize: 11.5,
              lineHeight: 1.5,
            }}
          >
            Authorized MTRCB personnel only. All access and activity is strictly monitored and logged
            in accordance with Republic Act No. 10173 (Data Privacy Act of 2012).
          </Typography>
        </Box>
      </Box>

      {/* Right Form Panel: Modernized Sign-in */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 3, sm: 6, md: 8 },
          backgroundColor: "rgba(0, 0, 0, 0.25)",
          backdropFilter: "blur(6px)",
          borderLeft: "1px solid rgba(255, 255, 255, 0.08)",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 400,
            backgroundColor: "#FFFFFF",
            p: { xs: 3.5, sm: 4.5 },
            borderRadius: "6px",
            border: "1px solid #E2E8F0",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.05)",
          }}
        >
          {/* Header */}
          <Stack spacing={1} mb={3.5}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "6px",
                backgroundColor: "#E0F2FE",
                color: "#0072BC",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1,
              }}
            >
              <LockOutlined sx={{ fontSize: 24 }} />
            </Box>
            <Typography
              component="h1"
              sx={{
                fontSize: 22,
                fontWeight: 800,
                letterSpacing: "-0.015em",
                color: "#0F172A",
              }}
            >
              Administrative Sign In
            </Typography>
            <Typography sx={{ color: "#64748B", fontSize: 13.5 }}>
              Enter your official administrative account details.
            </Typography>
          </Stack>

          {loginMessageError && (
            <Alert
              severity="error"
              sx={{
                mb: 2.5,
                borderRadius: "4px",
                fontSize: 13,
                border: "1px solid #FECACA",
              }}
            >
              {loginMessageError}
            </Alert>
          )}

          <form onSubmit={formik.handleSubmit} noValidate>
            <Stack spacing={2.5}>
              <TextField
                fullWidth
                size="small"
                label="Email address"
                type="email"
                autoComplete="email"
                autoFocus
                value={formik.values.email}
                onChange={formik.handleChange}
                name="email"
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                onBlur={formik.handleBlur}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "4px",
                    fontSize: 14,
                  },
                }}
              />

              <TextField
                fullWidth
                size="small"
                name="password"
                label="Password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={formik.values.password}
                onChange={formik.handleChange}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                onBlur={formik.handleBlur}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "4px",
                    fontSize: 14,
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                        sx={{ color: "#64748B" }}
                      >
                        {showPassword ? (
                          <VisibilityOffOutlined fontSize="small" />
                        ) : (
                          <VisibilityOutlined fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ pt: 0.5 }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      name="remember"
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                      sx={{
                        color: "#94A3B8",
                        "&.Mui-checked": {
                          color: "#0072BC",
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: 13, color: "#334155", fontWeight: 500 }}>
                      Remember credentials
                    </Typography>
                  }
                />
              </Stack>

              <LoadingButton
                fullWidth
                size="medium"
                type="submit"
                variant="contained"
                loading={formik.isSubmitting}
                sx={{
                  backgroundColor: "#0072BC",
                  color: "#FFFFFF",
                  fontWeight: 700,
                  fontSize: 14,
                  py: 1.2,
                  borderRadius: "4px",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#005a96",
                    boxShadow: "none",
                  },
                }}
              >
                Sign In
              </LoadingButton>
            </Stack>
          </form>
        </Box>
      </Box>
    </Box>
  );
}
